<template>
  <header class="header-bar">
    <div class="logo">
      <img :src="logoIcon" alt="logo"/>
      <span style="margin-left: 10px;">厉刻服视</span>
    </div>
    <div class="header-right-info">
      <div
          v-if="isLoggedIn && userInfo"
          class="user-info"
          ref="userInfoRef"
          @mouseenter="openMenu"
          @mouseleave="closeMenu">
        <img
            :src="userInfo.avatar || defaultAvatar"
            alt="avatar"
            class="avatar"
        />
        <span class="username">{{ userInfo.userName || userInfo.phone || '用户' }}</span>
        <!-- 用户菜单 -->
        <Teleport to="body">
          <div
              v-if="showMenu"
              class="user-menu"
              :style="menuStyle"
              @mouseenter="keepMenuOpen"
              @mouseleave="closeMenu"
          >
            <div class="menu-content">
              <div class="menu-item" @click="handleProfile">
                <div class="menu-icon">
                  <img :src="menuProfileIcon" alt="profile"/>
                </div>
                <span class="menu-text">个人信息</span>
              </div>
              <div class="menu-divider"></div>
              <div class="menu-item" @click="handleExchange">
                <div class="menu-icon">
                  <img :src="menuExchangeIcon" alt="exchange"/>
                </div>
                <span class="menu-text">兑换记录</span>
              </div>
              <div class="menu-divider"></div>
              <div class="menu-item" @click="handleLogoutClick">
                <div class="menu-icon">
                  <img :src="menuLogoutIcon" alt="logout"/>
                </div>
                <span class="menu-text">退出登录</span>
              </div>
            </div>
          </div>
        </Teleport>
      </div>
      <div
          v-else
          class="login"
          @click="emit('show-login')"
      >
        <img :src="defaultAvatar" alt="login"/>
        <span>请登录</span>
      </div>
      <div class="header-quota-part" v-if="isLoggedIn">
        <span class="quota-separator"> | </span>
        <span class="quota-chip">
        <img :src="userTimeIcon" alt="" class="quota-icon"/>
        <span>{{ quotaSummary.rest }}</span>
      </span>
      </div>
    </div>

  </header>

  <Teleport to="body">
    <div v-if="showLogoutConfirm" class="logout-mask" @click.self="closeLogoutConfirm">
      <div class="logout-modal">
        <div class="logout-title">确认退出登录？</div>
        <div class="logout-actions">
          <button class="logout-btn ghost" :disabled="logoutLoading" @click="closeLogoutConfirm">
            再考虑一下
          </button>
          <button class="logout-btn danger" :disabled="logoutLoading" @click="handleLogout">
            {{ logoutLoading ? '退出中...' : '确认退出' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <ProfileModal
      v-if="showProfileModal"
      :user-info="userInfo"
      :visible="showProfileModal"
      @close="showProfileModal = false"
      @update="handleProfileUpdate"
  />

  <ExchangeModal
      v-if="showExchangeModal"
      :visible="showExchangeModal"
      :current-times="quotaSummary.rest || 0"
      :total-times="quotaSummary.total"
      :records="exchangeRecords"
      :redeem-loading="redeemLoading"
      @close="showExchangeModal = false"
      @redeem="handleExchangeRedeem"
  />
</template>

<script setup>
/* global defineProps, defineEmits */
import {ref, onMounted, onUnmounted, watch} from 'vue';
import logoIcon from '@/assets/logo.png';
import defaultAvatar from '@/assets/header_default_avatar.png';
import menuProfileIcon from '@/assets/menu_profile.png';
import menuExchangeIcon from '@/assets/menu_exchange.png';
import menuLogoutIcon from '@/assets/menu_logout.png';
import userTimeIcon from '@/assets/user_time.svg';
import {doLogout, SUCCESS_CODE, useRedeemCode, listMyRedeemRecord, getQuotaSummary} from '@/api/api';
import Message from '@/utils/message';
import ProfileModal from './ProfileModal.vue';
import ExchangeModal from './ExchangeModal.vue';
import { onExchangeRequired } from '@/utils/exchangeModalBus';
import { onQuotaDelta } from '@/utils/quotaBus';

const props = defineProps({
  userInfo: {
    type: Object,
    default: null
  },
  isLoggedIn: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['show-login', 'logout', 'update-user-info']);

const showMenu = ref(false);
const showLogoutConfirm = ref(false);
const showProfileModal = ref(false);
const showExchangeModal = ref(false);
const logoutLoading = ref(false);
const redeemLoading = ref(false);
const exchangeRecords = ref([]);
const quotaSummary = ref({
  total: 0,
  used: 0,
  rest: 0
});
const userInfoRef = ref(null);
const menuStyle = ref({});
let closeTimer = null;
let disposeExchangeListener = null;
let disposeQuotaListener = null;

const openMenu = () => {
  // 清除可能存在的关闭定时器
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  if (userInfoRef.value) {
    updateMenuPosition();
  }
  showMenu.value = true;
};

const keepMenuOpen = () => {
  // 鼠标进入菜单时，清除关闭定时器
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
};

const closeMenu = () => {
  // 添加短暂延迟，避免鼠标快速移动时菜单闪烁
  closeTimer = setTimeout(() => {
    showMenu.value = false;
    closeTimer = null;
  }, 150);
};

const updateMenuPosition = () => {
  if (userInfoRef.value) {
    const rect = userInfoRef.value.getBoundingClientRect();
    const right = window.innerWidth - rect.left - 90;
    const top = rect.bottom + 10; // 减少间距，让鼠标更容易移动到菜单

    menuStyle.value = {
      top: `${top}px`,
      right: `${right}px`,
    };
  }
};

const handleProfile = () => {
  showProfileModal.value = true;
  showMenu.value = false;
};

const handleProfileUpdate = (updatedInfo) => {
  // 通知父组件更新用户信息
  emit('update-user-info', updatedInfo);
};

const fetchExchangeData = async () => {
  try {
    const [quotaRes, recordRes] = await Promise.all([
      getQuotaSummary(),
      listMyRedeemRecord(1, 20)
    ]);

    if (quotaRes && quotaRes.code === SUCCESS_CODE && quotaRes.data) {
      const data = quotaRes.data || {};
      quotaSummary.value = {
        total: Number.isFinite(Number(data.total)) ? Number(data.total) : 0,
        used: Number.isFinite(Number(data.used)) ? Number(data.used) : 0,
        rest: Number.isFinite(Number(data.rest)) ? Number(data.rest) : 0
      };
    }

    if (recordRes && recordRes.code === SUCCESS_CODE && recordRes.data) {
      const data = recordRes.data || {};
      let list = [];
      // 兼容多种分页返回结构：
      // 1. 直接返回数组
      // 2. MyBatis-Plus IPage: { records: [...] }
      // 3. 其它约定: { list: [...] }
      if (Array.isArray(data)) {
        list = data;
      } else if (Array.isArray(data.records)) {
        list = data.records;
      } else if (Array.isArray(data.list)) {
        list = data.list;
      }

      exchangeRecords.value = Array.isArray(list)
          ? list.map((item) => ({
            code: item.code,
            times: item.times ?? item.quota ?? item.count ?? 0,
            time: item.updateTime || item.createTime || item.time || item.redeemTime || ''
          }))
          : [];
    } else {
      exchangeRecords.value = [];
    }
  } catch (error) {
    console.error('获取兑换信息失败:', error);
    Message.error('获取兑换信息失败，请稍后重试');
  }
};

const handleExchange = async () => {
  showExchangeModal.value = true;
  showMenu.value = false;
  await fetchExchangeData();
};

const handleExchangeRedeem = (code) => {
  if (!code || redeemLoading.value) return;
  redeemLoading.value = true;
  useRedeemCode(code)
      .then((res) => {
        if (res && res.code === SUCCESS_CODE) {
          Message.success(res.msg || '兑换成功');
          // 兑换成功后刷新配额和兑换记录
          fetchExchangeData();
        } else {
          Message.error(res && res.msg ? res.msg : '兑换失败，请检查兑换码是否正确');
        }
      })
      .catch((error) => {
        console.error('兑换失败:', error);
        Message.error('兑换失败，请稍后重试');
      })
      .finally(() => {
        redeemLoading.value = false;
      });
};

const handleLogoutClick = () => {
  showLogoutConfirm.value = true;
  showMenu.value = false;
};

const closeLogoutConfirm = () => {
  if (logoutLoading.value) return;
  showLogoutConfirm.value = false;
};

const handleLogout = async () => {
  logoutLoading.value = true;
  try {
    const res = await doLogout();
    if (res.code === SUCCESS_CODE) {
      // 退出登录成功，通知父组件清除用户信息
      emit('logout');
    }
  } catch (error) {
    console.error('退出登录失败:', error);
    // 即使接口调用失败，也执行本地退出操作
    emit('logout');
  } finally {
    logoutLoading.value = false;
    showMenu.value = false;
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    showLogoutConfirm.value = false;
  }
};

const fetchQuotaSummary = async () => {
  try {
    const res = await getQuotaSummary();
    if (res && res.code === SUCCESS_CODE && res.data) {
      const data = res.data || {};
      quotaSummary.value = {
        total: Number.isFinite(Number(data.total)) ? Number(data.total) : 0,
        used: Number.isFinite(Number(data.used)) ? Number(data.used) : 0,
        rest: Number.isFinite(Number(data.rest)) ? Number(data.rest) : 0
      };
    }
  } catch (error) {
    console.error('获取配额统计失败:', error);
  }
};

watch(
    () => props.isLoggedIn,
    (val, oldVal) => {
      if (val && !oldVal) {
        // 登录后拉取一次配额统计
        fetchQuotaSummary();
      }
      if (!val) {
        // 退出登陆后重置
        quotaSummary.value = {
          total: 0,
          used: 0,
          rest: 0
        };
      }
    }
);

onMounted(() => {
  window.addEventListener('resize', updateMenuPosition);
  if (props.isLoggedIn) {
    fetchQuotaSummary();
  }
  disposeExchangeListener = onExchangeRequired(async () => {
    if (!props.isLoggedIn) {
      emit('show-login');
      return;
    }
    await handleExchange();
  });

  // 监听来自其他页面（例如 ChatPage）的配额变动事件，仅做前端数量调整
  disposeQuotaListener = onQuotaDelta((delta = -1) => {
    // 只有在已登录时才更新配额 UI
    if (!props.isLoggedIn) return;

    const currentRest = Number.isFinite(Number(quotaSummary.value.rest))
        ? Number(quotaSummary.value.rest)
        : 0;
    const currentUsed = Number.isFinite(Number(quotaSummary.value.used))
        ? Number(quotaSummary.value.used)
        : 0;
    const currentTotal = Number.isFinite(Number(quotaSummary.value.total))
        ? Number(quotaSummary.value.total)
        : 0;

    const safeDelta = Number.isFinite(Number(delta)) ? Number(delta) : -1;

    const nextRest = Math.max(0, currentRest + safeDelta);
    const nextUsed = Math.min(currentTotal, Math.max(0, currentUsed - safeDelta));

    quotaSummary.value = {
      total: currentTotal,
      used: nextUsed,
      rest: nextRest
    };
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateMenuPosition);
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  if (disposeExchangeListener) {
    disposeExchangeListener();
    disposeExchangeListener = null;
  }
  if (disposeQuotaListener) {
    disposeQuotaListener();
    disposeQuotaListener = null;
  }
});
</script>

<style scoped>
.header-bar {
  padding: 12px 60px;
  border-bottom: 1px solid #4A4136;
  background: rgba(255, 255, 255, 0.07);
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 16px;
  color: #e7e4df;
  z-index: 20;
  pointer-events: none;
}

.login:hover {
  opacity: .8;
}

.login {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  pointer-events: auto;
}

.user-info {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  pointer-events: auto;
  position: relative;
}

.user-info .avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
}

.user-info .username {
  font-size: 16px;
  color: #e7e4df;
}

.quota-separator {
  color: #7a7266;
  margin: 0 5px;
}

.quota-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
  padding: 0;
  font-size: 13px;
  color: #f7cf73;
  white-space: nowrap;
}

.quota-icon {
  width: 16px;
  vertical-align: bottom;
  height: 16px;
  display: inline-block;
}

.header-right-info {
  display: flex;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
}

.logo img {
  width: 30px;
  height: 30px;
}

.header-quota-part {
  display: flex;
  align-items: center;
}

.login img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.logout-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
}

.logout-modal {
  width: 300px;
  max-width: calc(100% - 48px);
  border-radius: 18px;
  background: linear-gradient(180deg, #2b241d 0%, #1a1510 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  padding: 28px 10px;
  box-sizing: border-box;
  text-align: center;
  color: #e7e4df;
}

.logout-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
}

.logout-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.logout-btn {
  min-width: 130px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid #fad87c;
  background: rgba(74, 65, 54, 0.6);
  color: #e7e4df;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
}

.logout-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.logout-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.logout-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.logout-btn.danger {
  background: linear-gradient(
      111deg,
      rgba(188, 130, 58, 1) 2%,
      rgba(247, 207, 115, 1) 40%,
      rgba(227, 156, 24, 1) 75%
  );
  color: #120f0d;
  border-color: #ddb463;
}
</style>

<style>
.user-menu {
  padding: 6px 20px;
  position: fixed;
  background: rgba(37, 32, 26, 1);
  border-radius: 16px;
  display: flex;
  font-size: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  box-sizing: border-box;
}

.user-menu .menu-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.user-menu .menu-item:hover {
  opacity: .8;
}

.user-menu .menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 16px;
  cursor: pointer;
  box-sizing: border-box;
  justify-content: flex-start;
}

.user-menu .menu-icon {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
  box-sizing: border-box;
}

.user-menu .menu-icon img {
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.user-menu .menu-text {
  margin-left: 10px;
  font-family: PingFang SC, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: rgba(243, 241, 236, 1);
  white-space: nowrap;
  line-height: normal;
  font-weight: normal;
}

.user-menu .menu-divider {
  width: 100%;
  min-height: 1px;
  background: rgb(74, 65, 54);
}
</style>

